import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { NavigationRoute, PHOTO_OVERLAY } from 'src/app/core/constants/constants';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { Subscription } from 'rxjs';
import { RegWorkflowEnum } from 'src/app/core/dtos/enum';

@Component({
  selector: 'app-photo-capture',
  templateUrl: './photo-capture.component.html',
  styleUrls: ['./photo-capture.component.scss']
})
export class PhotoCaptureComponent implements OnInit, OnDestroy {

  @Input() regId?: number;
  @Output() updateStatus = new EventEmitter<void>();

  cameraAccessErrorMessage: string;
  isIPhoneChrome: boolean;
  isPlaying = false;
  countdownTimeSeconds: number;
  capturePhotoError: string;
  captured: boolean;
  capturedPhoto: string;
  capturedPhotoFile: File;
  overlayPhoto: string;
  uploadErrorMessage: string;
  isUploading: boolean;
  isUploaded: boolean;
  capturedPhotoHeight = 600;
  pagePath = NavigationRoute.REGISTRATION.photoUpload;
  regWorkFlow: RegWorkflowEnum;
  uploadPhotoSubscription: Subscription;

  @ViewChild("video")
  public video: ElementRef;

  public constructor(
    private configHelper: ConfigHelper,
    private modalService: NgbModal,
    private registrationService: RegistrationService
  ) {
  }

  public ngOnInit() {
    this.regId = this.regId ? this.regId : 0;
    if (navigator.userAgent.match('CriOS')) {
      this.isIPhoneChrome = true;
    }
    this.overlayPhoto = this.configHelper.getImageUrl(PHOTO_OVERLAY);
    this.setupDevice();
  }

  async setupDevice() {
    const constraints = { audio: false, video: true };
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (stream) {
          this.video.nativeElement.srcObject = stream;
          this.video.nativeElement.play();
          if (stream.getVideoTracks()) {
            this.isPlaying = true;
          }

        } else {
          this.cameraAccessErrorMessage = "You have no output video device";
        }
      } catch (err) {
        this.cameraAccessErrorMessage = err.message;
        setTimeout(() => { this.setupDevice(); }, 5000);
      }
    }
  }

  startPhotoCaptureCountdown() {
    this.countdownTimeSeconds = 3;
    const cameraContainer = document.getElementById('camera-container') as HTMLElement;
    this.capturedPhotoHeight = cameraContainer.offsetHeight;
    const timer = setInterval(() => {
      if (this.countdownTimeSeconds > 0) {
        this.countdownTimeSeconds = this.countdownTimeSeconds - 1;
      } else {
        clearInterval(timer);
        this.capturePhotoShot();
      }
    }, 1000);
  }

  capturePhotoShot() {
    const targetHeight = Math.min(this.video.nativeElement.videoHeight, 640);
    const targetWidth = (targetHeight / 3) * 4;
    let scale = 1;
    if ((targetWidth / this.video.nativeElement.videoWidth) !== 1 && (targetHeight / this.video.nativeElement.videoHeight) !== 1) {
      scale = Math.min(targetWidth / this.video.nativeElement.videoWidth, targetHeight / this.video.nativeElement.videoHeight);
    }
    const width = this.video.nativeElement.videoWidth * scale;
    const height = this.video.nativeElement.videoHeight * scale;

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const xoffset = (canvas.width - width) / 2;
    const yoffset = Math.max((canvas.height - height) / 2, 0);
    ctx.drawImage(this.video.nativeElement, xoffset, yoffset, width, height);
    const dataURL = canvas.toDataURL('image/jpeg');
    if (dataURL === "data:,") {
      this.capturePhotoError = "Unable to capture photo. Please try again";
    }
    else {
      this.capturedPhoto = dataURL;
      this.captured = true;
      this.capturedPhotoFile = this.dataURLtoFile(dataURL, `${+new Date()}.jpg`);
    }
  }

  dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  rejectPhotoShot() {
    this.captured = false;
  }

  acceptPhotoShot() {
    this.isUploading = true;
    const formData = new FormData();
    formData.append("file", this.capturedPhotoFile, this.capturedPhotoFile.name);
    this.upload(formData);
  }

  upload(photo: FormData) {
    this.uploadPhotoSubscription = this.registrationService.uploadPhoto(this.regId, photo)
      .subscribe(
        () => {
          this.isUploading = false;
          this.isUploaded = true;
          setTimeout(() => {
            this.modalService.dismissAll();
          }, 3000);
          this.updateStatus.emit();
        },
        () => {
          this.isUploading = false;
          this.uploadErrorMessage = "Your photo could not be uploaded. Please check your internet connection and retry. Contact us for help if you cannot resolve the issue";
          setTimeout(() => {
            this.modalService.dismissAll();
          }, 5000);
        }
      );
  }

  ngOnDestroy(): void {
    this.uploadPhotoSubscription?.unsubscribe();
  }

}

